import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ShieldCheck, TrendingUp } from 'lucide-react';

const HIGHLIGHTS = [
  { icon: ShieldCheck, title: 'Pre-trade risk engine', body: 'Every order passes limits, kill switch and circuit breaker before it reaches the broker.' },
  { icon: Activity, title: 'Live market data', body: 'Streaming quotes and depth from your connected Kotak Neo session.' },
  { icon: TrendingUp, title: 'Strategy automation', body: 'Five built-in strategies with backtesting over your own price series.' },
];

/**
 * Shared frame for the signed-out screens: brand panel on the left (desktop
 * only), form card on the right.
 */
export function AuthLayout({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-canvas lg:grid lg:grid-cols-[1.1fr_1fr]">
      {/* Brand panel */}
      <aside className="relative hidden flex-col justify-between overflow-hidden border-r border-line bg-surface p-10 lg:flex xl:p-14">
        <div className="brand-wash pointer-events-none absolute inset-0 opacity-70" aria-hidden="true" />

        <Link to="/login" className="relative flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand">
            <TrendingUp className="h-4.5 w-4.5 text-white" aria-hidden="true" />
          </span>
          <span className="text-sm font-semibold tracking-tight text-fg">TradePlatform</span>
        </Link>

        <div className="relative max-w-md">
          <h2 className="text-2xl font-semibold tracking-tight text-balance text-fg">
            Algorithmic trading for Indian equity markets.
          </h2>
          <ul className="mt-8 flex flex-col gap-5">
            {HIGHLIGHTS.map(({ icon: Icon, title: t, body }) => (
              <li key={t} className="flex gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-line bg-surface-2">
                  <Icon className="h-3.5 w-3.5 text-brand-fg" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-medium text-fg">{t}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-fg-subtle">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-fg-faint">© 2026 TradePlatform</p>
      </aside>

      {/* Form panel */}
      <main className="flex min-h-screen items-center justify-center px-5 py-12 lg:min-h-0">
        <div className="w-full max-w-sm">
          <Link to="/login" className="mb-8 flex items-center justify-center gap-2.5 lg:hidden">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand">
              <TrendingUp className="h-4.5 w-4.5 text-white" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-fg">TradePlatform</span>
          </Link>

          <div className="animate-fade-in">
            <h1 className="text-xl font-semibold tracking-tight text-fg">{title}</h1>
            <p className="mt-1 text-sm text-fg-subtle">{description}</p>

            <div className="mt-7">{children}</div>

            {footer && <div className="mt-6 text-center text-sm text-fg-subtle">{footer}</div>}
          </div>
        </div>
      </main>
    </div>
  );
}
